import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePatchApiCompaniesCurrentServicemodulesIdIcon,
} from '@/api/generated/company-management-user/company-management-user'
import { notify } from '@/lib/notify'
import type { ServiceModuleModel, UpdateServiceModuleModel } from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import type { PlannedLanguages } from '@/components/common/translations/types.ts'
import { triggerAutoTranslation, useResolvedLanguages } from '@/components/common/translations/useAutoTranslate.ts'
import type {PropertyPlanConfig} from "@/components/common/translations/AutoTranslateAllButton.tsx";

const TRANSLATABLE_PROPERTIES = ['name']

export interface StandardModuleFieldsController {
  name: string
  setName: (v: string) => void
  iconName: string | null
  setIconName: (v: string | null) => void
  iconUrl: string | null
  iconBusy: boolean
  uploadIcon: (file: File, dataUrl: string) => void
  deleteIcon: () => void
  colorHex: string
  setColorHex: (v: string) => void
  isVisible: boolean
  setIsVisible: (v: boolean) => void
  sendNotification: boolean
  setSendNotification: (v: boolean) => void
  isCompanyWide: boolean
  setIsCompanyWide: (v: boolean) => void
  selectedGroupIds: number[]
  setSelectedGroupIds: (v: number[]) => void
  nameTranslations: Record<string, string>
  setNameTranslations: (v: Record<string, string>) => void
  saveTranslations: () => Promise<void>
  buildBasePayload: () => UpdateServiceModuleModel
  namePlannedLanguages: PlannedLanguages
  setNamePlannedLanguages: (v: PlannedLanguages) => void
  /** Fires the auto-translation job for `name`. Pass autoTranslateAll from the editor. */
  triggerTranslation: (entityId: string, autoTranslateAll: boolean) => void
  autoTranslateProperties: PropertyPlanConfig[]
}

export function useStandardServiceModuleFields(module: ServiceModuleModel): StandardModuleFieldsController {
  const { t } = useTranslation('service-modules')
  const [name, setName] = useState(module.name ?? '')
  const [iconName, setIconName] = useState<string | null>(module.iconName ?? null)
  const [iconUrl, setIconUrl] = useState<string | null>(module.iconUrl ?? null)
  const [colorHex, setColorHex] = useState(module.colorHex ?? '#43B8BB')
  const [isVisible, setIsVisible] = useState(module.isVisible ?? true)
  const [sendNotification, setSendNotification] = useState(module.sendNotification ?? false)
  const [isCompanyWide, setIsCompanyWide] = useState(module.isCompanyWideAssigned ?? true)
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(module.assignedGroupIds ?? [])
  const [namePlannedLanguages, setNamePlannedLanguages] = useState<PlannedLanguages>(new Set())

  const languages = useResolvedLanguages()
  const queryClient = useQueryClient()
  const patchIcon = usePatchApiCompaniesCurrentServicemodulesIdIcon()
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ServiceModule,
    entityId: String(module.id),
    properties: TRANSLATABLE_PROPERTIES,
  })

  async function uploadIcon(file: File, dataUrl: string) {
    try {
      await patchIcon.mutateAsync({ id: module.id, data: { File: file } })
      setIconUrl(dataUrl)
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
    } catch (error) {
      notify.apiError(error, t('toasts.imageUploadError'))
    }
  }

  async function deleteIcon() {
    try {
      await patchIcon.mutateAsync({ id: module.id, data: { File: undefined } })
      setIconUrl(null)
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
    } catch (error) {
      notify.apiError(error, t('toasts.imageRemoveError'))
    }
  }

  const buildBasePayload = (): UpdateServiceModuleModel => ({
    name: name || null,
    iconName: iconName || null,
    colorHex: colorHex || null,
    sendNotification,
    isVisible,
    isCompanyWideAssigned: isCompanyWide,
    assignedGroupIds: isCompanyWide ? [] : selectedGroupIds,
  })

  function triggerTranslation(entityId: string, autoTranslateAll: boolean) {
    const allLanguageCodes = languages.map((l) => l.code)
    triggerAutoTranslation({
      entityType: 'ServiceModule',
      entityId,
      properties: [
        {
          property: 'name',
          plannedLanguages: autoTranslateAll ? allLanguageCodes : [...namePlannedLanguages],
          existingTranslations: translations.get('name'),
        },
      ],
    })
  }

  return {
    name, setName,
    iconName, setIconName,
    iconUrl, iconBusy: patchIcon.isPending, uploadIcon, deleteIcon,
    colorHex, setColorHex,
    isVisible, setIsVisible,
    sendNotification, setSendNotification,
    isCompanyWide, setIsCompanyWide,
    selectedGroupIds, setSelectedGroupIds,
    nameTranslations: translations.get('name'),
    setNameTranslations: (v) => translations.set('name', v),
    saveTranslations: () => translations.save(String(module.id)),
    buildBasePayload,
    namePlannedLanguages,
    setNamePlannedLanguages,
    triggerTranslation,
    autoTranslateProperties: [
      {
        property: 'name',
        sourceValue: name,
        existingTranslations: translations.get('name'),
        manualTranslations: {},
        plannedLanguages: namePlannedLanguages,
        onPlannedChange: setNamePlannedLanguages,
      },
    ],
  }
}
